import {
  Banner,
  BannerAction,
  BannerClose,
  BannerContent,
  BannerDescription,
  BannerTitle,
} from '@heidi/design-system/banner';
import { Button } from '@heidi/design-system/button';
import { TierPracticeFilled, TierProFilled } from '@heidi/design-system/icons';
import { cn } from '@heidi/utils';
import { FormattedMessage, useIntl } from 'react-intl';

import { creditLimitReachedUpgradeMessages } from './credit-limit-reached-upgrade-messages';
import { getCreditLimitReachedUpgradePreset } from './credit-limit-reached-upgrade-presets';
import { renderCreditLimitReachedUpgradeCopy } from './render-credit-limit-reached-upgrade-copy';
import type { CreditLimitReachedUpgradeBannerProps } from './types';

export function creditUsageUpgradeMultiplier(isFreeUser: boolean): 10 | 5 {
  return isFreeUser ? 10 : 5;
}

/**
 * Dumb credit-limit upgrade banner. App wrappers supply visibility and upgrade wiring.
 */
export function CreditLimitReachedUpgradeBanner({
  plan,
  threshold,
  copy,
  isFreeUser = false,
  onUpgradeClick,
  hideCTA = false,
  isUpgradePending = false,
  dismissible: dismissibleOverride,
  onDismiss,
  className,
  'data-testid': testId = 'credit-limit-reached-upgrade-banner',
}: CreditLimitReachedUpgradeBannerProps) {
  const intl = useIntl();
  const {
    theme,
    messages,
    dismissible: presetDismissible,
  } = getCreditLimitReachedUpgradePreset(plan, threshold);
  const dismissible = dismissibleOverride ?? presetDismissible;
  const resolvedCopy = {
    title: copy?.title ?? messages.title,
    description: copy?.description ?? messages.description,
  };
  const titleValues = threshold !== 100 ? { usagePercent: threshold } : undefined;
  const descriptionValues = { multiplier: creditUsageUpgradeMultiplier(isFreeUser) };
  const TierIcon = plan === 'practiceMax' ? TierPracticeFilled : TierProFilled;
  const bannerIcon =
    plan === 'clinicianMax' ? (
      <TierProFilled
        size={18}
        className={cn('bannerIconClass' in theme ? theme.bannerIconClass : undefined)}
        aria-hidden
      />
    ) : undefined;

  const renderCtaLabel = () => {
    if (copy?.ctaLabel) {
      return renderCreditLimitReachedUpgradeCopy(copy.ctaLabel);
    }

    return (
      <FormattedMessage
        {...messages.ctaLabel}
        values={{ planName: intl.formatMessage(messages.ctaPlanName) }}
      />
    );
  };

  return (
    <Banner
      role="banner"
      subVariant={theme.bannerSubVariant}
      data-testid={testId}
      className={cn(
        'shadow-sm',
        'bannerClassName' in theme ? theme.bannerClassName : undefined,
        className
      )}
    >
      <BannerContent icon={bannerIcon}>
        <BannerTitle
          className={'bannerTitleClass' in theme ? theme.bannerTitleClass : undefined}
        >
          {renderCreditLimitReachedUpgradeCopy(resolvedCopy.title, titleValues)}
        </BannerTitle>
        <BannerDescription
          className={
            'bannerDescriptionClass' in theme ? theme.bannerDescriptionClass : undefined
          }
        >
          {renderCreditLimitReachedUpgradeCopy(
            resolvedCopy.description,
            descriptionValues
          )}
        </BannerDescription>
      </BannerContent>
      {!hideCTA && onUpgradeClick ? (
        <BannerAction>
          <Button
            variant={theme.buttonVariant}
            size="xs"
            className={'buttonClassName' in theme ? theme.buttonClassName : undefined}
            onClick={onUpgradeClick}
            disabled={isUpgradePending}
            data-testid={`${testId}-upgrade`}
            type="button"
          >
            <TierIcon size={12} className="shrink-0" />
            {renderCtaLabel()}
          </Button>
        </BannerAction>
      ) : null}
      {dismissible && onDismiss ? (
        <BannerClose
          onClick={onDismiss}
          aria-label={intl.formatMessage(
            creditLimitReachedUpgradeMessages.closeAriaLabel
          )}
          data-testid={`${testId}-close`}
        />
      ) : null}
    </Banner>
  );
}
